/**
 * 座標（フィールド・手札・認識領域の 4 点）の編集（Issue #780 で旧「撮影の設定」の画面から切り出した）。TetherCam のデバイスの設定
 * （pages/TetherCamDevice.js。Issue #790）が、そのデバイスの配信の絵の上で 2 席ぶんの枠を編集するのに使う。
 * synchro-client の config-points（OpenCV のピッカー）の Web 版で、計算は utils/fieldPicker.js（Python と同じ式）。
 *
 * - 左: 元の 1 枚と点。ホイールで拡大（カーソルの位置を中心に）、何も無いところのドラッグで移動、角のドラッグで点を動かす。
 *   矢印キーで 1 画素（Shift で 10 画素）、Tab で次の角。矩形モードは角で拡縮（対角を固定）・上の丸で回転・中をドラッグで移動
 * - 右: 枠ごとの設定と、補正後の絵（4 点モードはプレイマット画像と半分ずつ重ねる。config-points の Preview と同じ）
 * - 保存は呼び手の onSave(slots)（画素の点。正規化は呼び手）
 *
 * 画像は <img> を CSS の transform で動かすだけ（canvas で画素を読まないので CORS の設定は要らない）。
 *
 * props:
 *   width / height   元の 1 枚の大きさ（配信の絵の大きさ）
 *   slotDefs         枠の一覧 [{fieldType, player, label}]（utils/fieldPicker の SLOTS の一部）
 *   initialSlots     画面の状態（fromDoc の形。slotKey → {enabled, points, rectMode, rectMaxWidth, rectMaxHeight, follow（手札だけ）}）
 *   liveUrl          生の映像の動画（/live/stream?raw=1 など）。stillUrl は 1 枚（/live/raw.jpg など。t を足して読み直す）
 *   title            右の見出し
 *   onSave(slots)    保存（失敗は投げる）
 *   replace          {seq, slots, message?}: seq が変わったらその枠を差し替える（自動で合わせた結果。Issue #802）
 *   renderActions(slots)  保存の横に足すもの（自動で合わせるのボタン）
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  PLAYMATS, angleFromHandle, defaultQuad, lockAspect, playmatSize, rectCorners, rectFromCorners,
  resizeRectCorner, rotationHandle, slotKey,
} from '../utils/fieldPicker';
import { useLiveVideo } from './liveCorrect';
import WarpCheck from './WarpCheck';
import WarpCanvas from './WarpCanvas';

/** 配信の生の映像を動画で見られるか。VideoDecoder が要る */
const canLive = () => typeof VideoDecoder !== 'undefined';   // eslint-disable-line no-undef
const LIVE = 'live';      // 動画
const STILL = 'still';    // 1 枚（撮り直すまで止まったまま。VideoDecoder が無くてよい）

export const SLOT_COLORS = {
  'field:1': '#4da3ff', 'field:2': '#ff5a5a', 'hand:1': '#7fe0a0', 'hand:2': '#ffc04d',
  'recog1:1': '#b48cff', 'recog2:1': '#d9b8ff', 'recog1:2': '#ff8cc6', 'recog2:2': '#ffc2e0',
};
const HIT_PX = 14;          // 画面の画素で、この距離までは角を掴む
const PREVIEW_W = 440;

function insidePolygon([x, y], pts) {
  let inside = false;
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i, i += 1) {
    const [xi, yi] = pts[i];
    const [xj, yj] = pts[j];
    if ((yi > y) !== (yj > y) && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

const withT = (url, t) => `${url}${url.includes('?') ? '&' : '?'}t=${t}`;

export default function QuadSetupEditor({
  width: W, height: H, slotDefs, initialSlots, liveUrl, stillUrl, title, onSave, replace = null, renderActions = null,
}) {
  const [source, setSource] = useState(() => (canLive() ? LIVE : STILL));
  const [stillAt, setStillAt] = useState(() => Date.now());   // 1 枚を撮った時刻（url に付けて読み直させる）
  const liveCanvas = useRef(null);
  const [slots, setSlots] = useState(initialSlots);
  const firstKey = slotKey(slotDefs[0].fieldType, slotDefs[0].player);
  const [active, setActive] = useState(firstKey);
  const [selected, setSelected] = useState(0);
  const [view, setView] = useState({ zoom: 1, x: 0, y: 0 });
  const [dirty, setDirty] = useState(false);
  const [message, setMessage] = useState(null);
  const [blend, setBlend] = useState(true);
  const [checking, setChecking] = useState(false);   // 大きく重ねて確認（左側いっぱい）
  const [aspectLock, setAspectLock] = useState(true);
  const boxRef = useRef(null);
  const drag = useRef(null);

  const live = source === LIVE;
  const stillSrc = withT(stillUrl, stillAt);

  // 配信の生の映像を canvas に（4 点を選ぶ背景）。幅が変わったら繋ぎ直す
  useLiveVideo(liveCanvas, liveUrl, live && !!W, W);

  // 枠の一覧が変わったら（席を替えた）その枠の状態に
  useEffect(() => { setSlots(initialSlots); setActive(firstKey); setSelected(0); setDirty(false); setMessage(null); },
    [initialSlots, firstKey]);

  // 自動で合わせた結果（保存は人が押してから。Issue #802）
  const lastReplace = useRef(null);
  useEffect(() => {
    if (!replace || replace.seq === lastReplace.current) return;
    lastReplace.current = replace.seq;
    const first = Object.keys(replace.slots ?? {})[0];
    if (first) {             // 見つからなかった・届かなかったときは言葉だけ（枠は変えない）
      setSlots((cur) => ({ ...cur, ...replace.slots }));
      setActive(first);
      setSelected(0);
      setDirty(true);
    }
    setMessage(replace.message ?? '自動で合わせました（保存すると使い始めます）');
  }, [replace]);

  // 点は画素で持つので、配信の大きさが変わったら割合のまま写す
  const dims = useRef([W, H]);
  useEffect(() => {
    const [pw, ph] = dims.current;
    if (pw === W && ph === H) return;
    dims.current = [W, H];
    setSlots((cur) => Object.fromEntries(Object.entries(cur).map(([k, v]) => [k, {
      ...v, points: v.points?.map(([x, y]) => [(x * W) / pw, (y * H) / ph]),
    }])));
  }, [W, H]);

  const fit = useCallback(() => {
    const box = boxRef.current?.getBoundingClientRect();
    if (!box?.width || !W) { setView({ zoom: 1, x: 0, y: 0 }); return; }
    const zoom = Math.min(box.width / W, box.height / H);
    setView({ zoom, x: (box.width - W * zoom) / 2, y: (box.height - H * zoom) / 2 });
  }, [W, H]);
  useEffect(() => { fit(); }, [fit]);

  const st = slots?.[active];
  const color = SLOT_COLORS[active];
  const fieldType = active.split(':')[0];
  const points = st?.points;
  const rect = useMemo(() => (st?.rectMode && points ? rectFromCorners(points) : null), [st, points]);

  const update = useCallback((patch) => {
    setSlots((cur) => ({ ...cur, [active]: { ...cur[active], ...patch } }));
    setDirty(true);
    setMessage(null);
  }, [active]);
  const setPoints = useCallback((pts) => update({ points: pts }), [update]);
  const rectAspect = st?.rectMode && aspectLock ? (st.rectMaxWidth || 1) / (st.rectMaxHeight || 1) : null;

  // --- 座標 ---
  const toImage = (e) => {
    const box = boxRef.current.getBoundingClientRect();
    return [(e.clientX - box.left - view.x) / view.zoom, (e.clientY - box.top - view.y) / view.zoom];
  };

  const onPointerDown = (e) => {
    if (!points) return;
    const p = toImage(e);
    const hit = HIT_PX / view.zoom;
    const near = points.map((q, i) => [Math.hypot(q[0] - p[0], q[1] - p[1]), i]).sort((a, b) => a[0] - b[0])[0];
    e.currentTarget.setPointerCapture?.(e.pointerId);
    if (e.button === 0 && rect) {
      const hnd = rotationHandle(rect, 30);
      if (Math.hypot(hnd[0] - p[0], hnd[1] - p[1]) <= hit) { drag.current = { kind: 'rotate' }; return; }
      if (near[0] <= hit) { drag.current = { kind: 'corner', i: near[1] }; setSelected(near[1]); return; }
      if (insidePolygon(p, points)) { drag.current = { kind: 'move', from: p, rect }; return; }
    } else if (e.button === 0 && near[0] <= hit) {
      drag.current = { kind: 'corner', i: near[1] };
      setSelected(near[1]);
      return;
    }
    drag.current = { kind: 'pan', from: [e.clientX, e.clientY], view };
  };

  const onPointerMove = (e) => {
    const d = drag.current;
    if (!d) return;
    if (d.kind === 'pan') {
      setView({ ...d.view, x: d.view.x + e.clientX - d.from[0], y: d.view.y + e.clientY - d.from[1] });
      return;
    }
    const [x, y] = toImage(e);
    if (d.kind === 'corner' && rect) setPoints(rectCorners(resizeRectCorner(rect, d.i, x, y, rectAspect)));
    else if (d.kind === 'corner') setPoints(points.map((q, i) => (i === d.i ? [x, y] : q)));
    else if (d.kind === 'rotate') setPoints(rectCorners({ ...rect, angle: angleFromHandle(rect, x, y) }));
    else if (d.kind === 'move') setPoints(rectCorners({ ...d.rect, cx: d.rect.cx + x - d.from[0], cy: d.rect.cy + y - d.from[1] }));
  };
  const onPointerUp = () => { drag.current = null; };

  const onWheel = useCallback((e) => {
    e.preventDefault();
    const box = boxRef.current.getBoundingClientRect();
    const mx = e.clientX - box.left;
    const my = e.clientY - box.top;
    setView((v) => {
      const zoom = Math.min(Math.max(v.zoom * 1.0015 ** -e.deltaY, 0.02), 16);
      return { zoom, x: mx - ((mx - v.x) * zoom) / v.zoom, y: my - ((my - v.y) * zoom) / v.zoom };
    });
  }, []);
  useEffect(() => {
    const el = boxRef.current;
    if (!el) return undefined;
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [onWheel, W]);

  const onKeyDown = (e) => {
    if (!points || e.target.tagName === 'INPUT' || e.target.tagName === 'SELECT') return;
    const step = e.shiftKey ? 10 : 1;
    const dir = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step] }[e.key];
    if (dir) {
      e.preventDefault();
      if (rect && e.altKey) setPoints(rectCorners({ ...rect, cx: rect.cx + dir[0], cy: rect.cy + dir[1] }));   // Alt で矩形ごと
      else if (rect) {
        // 矩形モードも選んでいる角を動かす（対角を固定して拡縮。縦横比の固定はそのまま）
        const q = points[selected];
        setPoints(rectCorners(resizeRectCorner(rect, selected, q[0] + dir[0], q[1] + dir[1], rectAspect)));
      } else setPoints(points.map((q, i) => (i === selected ? [q[0] + dir[0], q[1] + dir[1]] : q)));
    } else if (e.key === 'Tab') {
      e.preventDefault();
      setSelected((s) => (s + (e.shiftKey ? 3 : 1)) % 4);
    } else if (rect && (e.key === '[' || e.key === ']')) {
      setPoints(rectCorners({ ...rect, angle: rect.angle + ((e.key === '[' ? -0.5 : 0.5) * Math.PI) / 180 }));
    }
  };

  const save = async () => {
    try {
      await onSave(slots);
      setDirty(false);
      setMessage('保存しました');
    } catch (err) {
      setMessage(`保存できませんでした: ${err?.message ?? err}`);
    }
  };

  const [pw, ph] = points ? playmatSize(fieldType, points, { rectMode: st.rectMode, rectMaxHeight: st.rectMaxHeight }) : [1, 1];
  const scale = PREVIEW_W / pw;
  const r = 8 / view.zoom;
  const sw = 2 / view.zoom;

  return (
    <div data-testid="shoot-setup" style={{ display: 'flex', flex: 1, minHeight: 0, background: '#0b0f18', color: '#e8eefc' }}>
      <div
        ref={boxRef} tabIndex={0} data-testid="shoot-setup-source" onKeyDown={onKeyDown}
        onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp} onPointerCancel={onPointerUp}
        style={{ flex: 1, position: 'relative', overflow: 'hidden', cursor: drag.current?.kind === 'pan' ? 'grabbing' : 'crosshair', outline: 'none', touchAction: 'none' }}
      >
        <div style={{ position: 'absolute', left: 0, top: 0, transformOrigin: '0 0', transform: `translate(${view.x}px, ${view.y}px) scale(${view.zoom})` }}>
          {live
            ? <canvas ref={liveCanvas} data-testid="shoot-setup-live" style={{ display: 'block', width: W, height: H, background: '#000' }} />
            : <img src={stillSrc} alt="配信の 1 枚" data-testid="shoot-setup-still" width={W} height={H} draggable={false} style={{ display: 'block', userSelect: 'none' }} />}
          <svg width={W} height={H} style={{ position: 'absolute', left: 0, top: 0, overflow: 'visible' }}>
            {slotDefs.map(({ fieldType: t, player }) => {
              const k = slotKey(t, player);
              const s = slots?.[k];
              if (!s?.enabled || !s.points || k === active) return null;
              return <polygon key={k} points={s.points.map((q) => q.join(',')).join(' ')} fill="none" stroke={SLOT_COLORS[k]} strokeWidth={sw} strokeDasharray={`${6 / view.zoom}`} opacity={0.6} />;
            })}
            {points ? (
              <g data-testid="active-quad">
                <polygon points={points.map((q) => q.join(',')).join(' ')} fill={`${color}22`} stroke={color} strokeWidth={sw} />
                {rect ? (() => {
                  const top = [(points[0][0] + points[1][0]) / 2, (points[0][1] + points[1][1]) / 2];
                  const h = rotationHandle(rect, 30);
                  return (
                    <>
                      <line x1={top[0]} y1={top[1]} x2={h[0]} y2={h[1]} stroke={color} strokeWidth={sw} />
                      <circle cx={h[0]} cy={h[1]} r={r} fill={color} />
                    </>
                  );
                })() : null}
                {points.map((q, i) => (
                  <g key={i}>
                    <circle cx={q[0]} cy={q[1]} r={r} fill={i === selected ? '#fff' : color} stroke="#000" strokeWidth={sw / 2} />
                    <text x={q[0] + r * 1.4} y={q[1] - r * 1.4} fontSize={14 / view.zoom} fill="#fff" stroke="#000" strokeWidth={sw / 3}>{['左上', '右上', '右下', '左下'][i]}</text>
                  </g>
                ))}
              </g>
            ) : null}
          </svg>
        </div>
        {checking && points ? (
          <WarpCheck points={points} pw={pw} ph={ph} selected={selected} color={color}
            imgSrc={live ? null : stillSrc} liveCanvasRef={live ? liveCanvas : null}
            playmatSrc={st.rectMode ? null : PLAYMATS[fieldType].src}
            onKeyDown={onKeyDown} onSelect={setSelected} onClose={() => { setChecking(false); boxRef.current?.focus?.(); }} />
        ) : null}
        <div style={{ position: 'absolute', left: 8, bottom: 8, fontSize: 12, background: '#000a', padding: '4px 8px', borderRadius: 4 }}>
          ホイールで拡大・何も無いところをドラッグで移動・角をドラッグ・矢印キーで選んでいる角を 1 画素（Shift で 10）・Tab で次の角{rect ? '・Alt+矢印で矩形ごと・[ ] で回転' : ''}
          <button type="button" onClick={fit} style={{ marginLeft: 8 }}>全体</button>
        </div>
      </div>

      <div style={{ width: PREVIEW_W + 32, padding: 16, overflowY: 'auto', borderLeft: '1px solid #223' }}>
        <h3 style={{ marginTop: 0 }}>{title}</h3>
        <label style={{ fontSize: 12 }}>元の画像（配信 {W}x{H}）:{' '}
          <select value={source} onChange={(e) => setSource(e.target.value)} data-testid="source-select">
            {canLive() ? <option value={LIVE}>動画</option> : null}
            <option value={STILL}>1 枚</option>
          </select>
        </label>
        {!live ? <button type="button" onClick={() => setStillAt(Date.now())} style={{ marginLeft: 8 }} data-testid="still-retake">撮り直す</button> : null}

        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', margin: '12px 0' }}>
          {slotDefs.map(({ fieldType: t, player, label }) => {
            const k = slotKey(t, player);
            return (
              <button key={k} type="button" data-testid={`slot-${k}`} onClick={() => { setActive(k); setSelected(0); }}
                style={{ padding: '4px 10px', borderRadius: 6, border: `2px solid ${SLOT_COLORS[k]}`, background: k === active ? SLOT_COLORS[k] : 'transparent', color: k === active ? '#000' : '#e8eefc', opacity: slots?.[k]?.enabled ? 1 : 0.5 }}>
                {label}
              </button>
            );
          })}
        </div>

        {st ? (
          <div style={{ display: 'grid', gap: 8, fontSize: 13 }}>
            <label><input type="checkbox" checked={!!st.enabled} onChange={(e) => update({ enabled: e.target.checked })} data-testid="slot-enabled" /> この枠を使う</label>
            {fieldType === 'hand' ? (
              <label title="補正サーバーが 2 回/秒カードの位置を探し、持っているカードが枠の縦の真ん中に来るよう枠を上下させる（取れないときは今の位置のまま）">
                <input type="checkbox" checked={!!st.follow} onChange={(e) => update({ follow: e.target.checked })} data-testid="slot-follow" />
                {' '}カードに追従（ライブで枠を上下させる）
              </label>
            ) : null}
            <div>
              <label><input type="radio" checked={!st.rectMode} onChange={() => update({ rectMode: false })} /> 4 点</label>{' '}
              <label><input type="radio" checked={!!st.rectMode} onChange={() => update({ rectMode: true, points: rectCorners(rectFromCorners(points)) })} /> 矩形</label>
            </div>
            {st.rectMode ? (
              <div>
                目標の大きさ（参考の幅 × 高さ）{' '}
                <input type="number" value={st.rectMaxWidth} onChange={(e) => update({ rectMaxWidth: Number(e.target.value) || 1 })} style={{ width: 70 }} /> ×{' '}
                <input type="number" value={st.rectMaxHeight} onChange={(e) => update({ rectMaxHeight: Number(e.target.value) || 1 })} style={{ width: 70 }} />
                <label style={{ marginLeft: 8 }}>
                  <input type="checkbox" checked={aspectLock} onChange={(e) => {
                    setAspectLock(e.target.checked);
                    if (e.target.checked && rect) setPoints(rectCorners(lockAspect(rect, (st.rectMaxWidth || 1) / (st.rectMaxHeight || 1))));
                  }} /> 縦横比を固定
                </label>
              </div>
            ) : (
              <div>
                <button type="button" onClick={() => setPoints([points[1], points[2], points[3], points[0]])}>並びを回す</button>{' '}
                <span style={{ color: '#8a93a8' }}>選択中: {['左上', '右上', '右下', '左下'][selected]}</span>
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button type="button" onClick={() => setChecking((c) => !c)} data-testid="warp-check-open" style={{ fontWeight: 700 }}>
                {checking ? '重ねて確認を閉じる' : 'プレイマットと大きく重ねて確認'}
              </button>
              <button type="button" onClick={() => setPoints(defaultQuad(W, H))}>点を初期位置に戻す</button>
            </div>

            {live ? (
              <div style={{ color: '#8a93a8', fontSize: 12 }}>補正後（{pw}×{ph}）の絵は「プレイマットと大きく重ねて確認」で見られます</div>
            ) : (
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>補正後（{pw}×{ph}）</span>
                  {!st.rectMode ? <label style={{ fontSize: 12 }}><input type="checkbox" checked={blend} onChange={(e) => setBlend(e.target.checked)} /> プレイマットと重ねる</label> : null}
                </div>
                <div data-testid="preview" style={{ width: PREVIEW_W, height: ph * scale, position: 'relative', overflow: 'hidden', background: '#000', marginTop: 4 }}>
                  <div style={{ position: 'absolute', left: 0, top: 0, width: pw, height: ph, transformOrigin: '0 0', transform: `scale(${scale})`, overflow: 'hidden' }}>
                    {points && !live ? <WarpCanvas data-testid="preview-warp" points={points} pw={pw} ph={ph} imgSrc={stillSrc} style={{ position: 'absolute', left: 0, top: 0 }} /> : null}
                    {!st.rectMode && blend && PLAYMATS[fieldType].src ? <img src={PLAYMATS[fieldType].src} alt="" width={pw} height={ph} style={{ position: 'absolute', left: 0, top: 0, opacity: 0.5 }} /> : null}
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : null}

        <div style={{ marginTop: 16, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <button type="button" data-testid="save-setup" disabled={!dirty} onClick={save} style={{ padding: '6px 16px', fontWeight: 700 }}>保存</button>
          {renderActions ? renderActions(slots) : null}
          <span data-testid="setup-message" style={{ fontSize: 12, color: '#8a93a8' }}>{message ?? (dirty ? '保存していない変更があります' : '')}</span>
        </div>
      </div>
    </div>
  );
}

